import { z } from 'zod'
import { createClient, ContentfulClientApi } from 'contentful'

const ContentfulClientEnvVarsSchema = z.object({
  CONTENTFUL_SPACE_ID: z.string(),
  CONTENTFUL_ACCESS_TOKEN: z.string(),
  CONTENTFUL_PREVIEW_ACCESS_TOKEN: z.string(),
  CONTENTFUL_ENVIRONMENT: z.string(),
})

type CreateClientArgs = { preview?: boolean }
const PREVIEW_HOST_URL = 'preview.contentful.com'

// Cache Contentful client instances to avoid recreating them on every request
// This significantly improves performance, especially for Next.js API routes
const clientCache = new Map<boolean, ContentfulClientApi<undefined>>()

// Using a contentful client from their SDK which calls their rest api
// See new services and docs https://contentful.github.io/contentful.js for usage examples
const createContentfulClient = ({
  preview,
}: CreateClientArgs = {}): ContentfulClientApi<undefined> => {
  // Check if we already have a cached client for this preview mode
  const cacheKey = Boolean(preview)
  const cachedClient = clientCache.get(cacheKey)
  if (cachedClient) {
    return cachedClient
  }

  // Parse environment variables once
  const envVars = ContentfulClientEnvVarsSchema.parse({
    CONTENTFUL_SPACE_ID: process.env.CONTENTFUL_SPACE_ID,
    CONTENTFUL_ACCESS_TOKEN: process.env.CONTENTFUL_ACCESS_TOKEN,
    CONTENTFUL_PREVIEW_ACCESS_TOKEN: process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN,
    CONTENTFUL_ENVIRONMENT: process.env.CONTENTFUL_ENVIRONMENT,
  })

  // Create and cache the client
  const client = createClient({
    accessToken: preview
      ? envVars.CONTENTFUL_PREVIEW_ACCESS_TOKEN
      : envVars.CONTENTFUL_ACCESS_TOKEN,
    environment: envVars.CONTENTFUL_ENVIRONMENT,
    space: envVars.CONTENTFUL_SPACE_ID,
    ...(preview ? { host: PREVIEW_HOST_URL } : {}),
  })

  clientCache.set(cacheKey, client)
  return client
}

export default createContentfulClient
